import { useMemo, type FC } from 'react'

import { Separator } from '@wartek-id/separator'
import { Badge } from '@wartek-id/badge'

import EvaluationResult from 'app/KinerjaKSPage/components/EvaluationResultV2'
import StateEmpty from 'app/KinerjaShared/components/StateEmpty'
import {
  useApiGetRiwayatHasilEvaluasiTahunan,
  useApiGetRiwayatHasilEvaluasiTahunanGuru,
} from 'api/kinerja/kepala-sekolah/riwayat'
import CardStateError from 'app/KinerjaShared/components/CardStateError'
import { NotFoundError } from 'utils/customError'
import { PREFIX_RIWAYAT_GURU } from 'app/KinerjaKSPage/constants'
import BannerStatusPengaliranAkhir from 'app/KinerjaPage/components/BannerStatusPengaliranAkhir'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'

import type { TRiwayatId } from 'types/kinerja-riwayat-ks'

type EvaluationResultV2YearProps = {
  riwayatId: TRiwayatId
  hasRootHeader?: boolean
  fullPage?: boolean
  className?: string
  npsn?: string
}

const EvaluationResultV2Year: FC<EvaluationResultV2YearProps> = ({
  riwayatId,
  hasRootHeader = false,
  fullPage = false,
  className,
  npsn,
}) => {
  /**
   * Determines if the `riwayatId` belongs to a "Guru" (teacher) and extracts the relevant ID.
   *
   * @returns {[boolean, string]} A tuple where the first element is a boolean indicating if the `riwayatId` belongs to a "Guru",
   * and the second element is the processed `riwayatId` (without the "G-" prefix if it belongs to a "Guru").
   */
  const [isGuru, riwayatIdGuru] = useMemo(() => {
    const isGuru = riwayatId.startsWith(PREFIX_RIWAYAT_GURU)
    const riwayatIdGuru = isGuru
      ? riwayatId.slice(PREFIX_RIWAYAT_GURU.length)
      : riwayatId

    return [isGuru, riwayatIdGuru]
  }, [riwayatId])

  const { profile } = useRoleContext()
  const showSinkroStatus = useMemo(() => {
    // Always show the sinkro status for Guru.
    // We dont have to check the `role.type`
    // since it already have check mechanism via riwayatId
    if (isGuru) return true

    // Set default value is false
    if (!Array.isArray(profile?.roles) || !npsn) return false

    const currentSchool = profile.roles.find((role) => role.npsn === npsn)
    return currentSchool?.type !== 'KS_PLT'
  }, [profile, npsn, isGuru])

  const {
    data: { data: resultKs, meta: metaKs } = { data: null, meta: null },
    isLoading: isHasilEvaluasiKsLoading,
    isSuccess: isHasilEvaluasiKsSuccess,
    isError: isHasilEvaluasiKsError,
  } = useApiGetRiwayatHasilEvaluasiTahunan(riwayatId, {
    enabled: !!riwayatId && !isGuru,
  })

  const {
    data: { data: resultGuru, meta: metaGuru } = { data: null, meta: null },
    isLoading: isHasilEvaluasiGuruLoading,
    isSuccess: isHasilEvaluasiGuruSuccess,
    isError: isHasilEvaluasiGuruError,
  } = useApiGetRiwayatHasilEvaluasiTahunanGuru(riwayatIdGuru, {
    enabled: !!riwayatIdGuru && isGuru,
  })

  const isLoading = isGuru
    ? isHasilEvaluasiGuruLoading
    : isHasilEvaluasiKsLoading
  const isHasilEvaluasiSuccess = isGuru
    ? isHasilEvaluasiGuruSuccess
    : isHasilEvaluasiKsSuccess
  const isHasilEvaluasiError = isGuru
    ? isHasilEvaluasiGuruError
    : isHasilEvaluasiKsError
  const result = isGuru ? resultGuru : resultKs
  const meta = isGuru ? metaGuru : metaKs

  const title = isGuru
    ? 'Hasil Penilaian Kinerja Guru'
    : 'Hasil Penilaian Kinerja Kepala Sekolah'

  // Define the data to be shown in the page
  const mappedResult = useMemo(() => {
    if (!result) return null

    const supervisorData = isGuru ? result?.supervisor : result?.dinas

    return {
      details: [
        {
          label: 'Pegawai yang Dinilai',
          items: [
            {
              label: 'Nama',
              value: isGuru ? result?.guru?.nama : result?.ks?.nama,
            },
            {
              label: 'NIP',
              value: isGuru ? result?.guru?.nip : result?.ks?.nip,
            },
            {
              label: 'Jabatan',
              value: isGuru ? result?.guru?.jabatan : result?.ks?.jabatan,
            },
            {
              label: 'Unit Kerja',
              value: isGuru ? result?.guru?.unitKerja : result?.ks?.unitKerja,
            },
          ],
        },
        {
          label: 'Pejabat Penilai Kinerja',
          items: [
            { label: 'Nama', value: supervisorData?.nama },
            { label: 'NIP', value: supervisorData?.nip },
            { label: 'Jabatan', value: supervisorData?.jabatan },
            { label: 'Unit Kerja', value: supervisorData?.unitKerja },
          ],
        },
        ,
      ],
      summary: {
        label: 'Predikat Kinerja',
        value: result?.predikatKinerja?.predikatKinerja,
      },
    }
  }, [isGuru, result])

  if (!riwayatId) return <StateEmpty />
  if (!isLoading && isHasilEvaluasiError) {
    // If displayed as a full page, throw a 404 error
    if (fullPage) throw new NotFoundError('', 'Not Found')
    // Else display the error state
    else return <CardStateError />
  }

  return (
    <>
      <EvaluationResult.Root className={className}>
        {hasRootHeader && <EvaluationResult.Header title={title} />}

        <div>
          {showSinkroStatus && (
            <BannerStatusPengaliranAkhir
              year={meta?.skpYear}
              className="mb-6"
            />
          )}
          <EvaluationResult.Content>
            {isLoading && <EvaluationResult.Spinner />}

            {isHasilEvaluasiError && <EvaluationResult.Error />}

            {isHasilEvaluasiSuccess && (
              <EvaluationResult.Result.Root>
                {/* Holds the Header */}
                <EvaluationResult.Result.Header title={title} period={3} />

                <EvaluationResult.Result.Content>
                  {/* Holds the "Nama Instansi" and "Periode Penilaian" */}
                  <EvaluationResult.Result.Info
                    name={result?.namaInstansi}
                    customPeriod={
                      <>1 Januari sampai dengan 31 Desember {meta?.skpYear}</>
                    }
                  />

                  <Separator />

                  {/* Holds the "Pegawai yang Dinilai" and "Pejabat Penilai Kinerja" */}
                  <EvaluationResult.Result.Details
                    data={mappedResult?.details}
                  />

                  {/* Holds the "Predikat Kinerja", "Poin Pengembangan Kompetensi", and footnote */}
                  <EvaluationResult.Result.Summary
                    period={3}
                    data={{
                      badge: (
                        <Badge
                          variant="informational"
                          className="!bg-blue-90 !text-inverse"
                        >
                          Tahunan
                        </Badge>
                      ),
                      summary: mappedResult?.summary,
                    }}
                  />
                </EvaluationResult.Result.Content>
              </EvaluationResult.Result.Root>
            )}
          </EvaluationResult.Content>
        </div>
      </EvaluationResult.Root>
    </>
  )
}
export default EvaluationResultV2Year
